import React from 'react';
import DocLayout from '../DocLayout';
import { LayoutDashboard, BarChart3, Target } from 'lucide-react';

const Dashboard = () => {
  return (
    <DocLayout title="Understanding Your Dashboard" category="Getting Started">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <LayoutDashboard className="w-6 h-6 text-indigo-600" />
            Dashboard Overview
          </h2>
          <p className="text-slate-700 mb-4">
            Your I-Match dashboard provides a comprehensive view of your sales pipeline and activities.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Main Navigation</h3>
          <p className="text-slate-700 mb-3">
            The left sidebar provides quick access to all major features:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">🏠 Dashboard</h4>
              <p className="text-slate-600 text-xs">Overview and quick stats</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">🎯 Leads</h4>
              <p className="text-slate-600 text-xs">All your leads in one place</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">🏢 Companies</h4>
              <p className="text-slate-600 text-xs">Company database</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">👥 Contacts</h4>
              <p className="text-slate-600 text-xs">Individual contact records</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">✅ Tasks</h4>
              <p className="text-slate-600 text-xs">Task management</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">📅 Calendar</h4>
              <p className="text-slate-600 text-xs">Event scheduling</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">📧 Email Sync</h4>
              <p className="text-slate-600 text-xs">Gmail integration</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">📊 Analytics</h4>
              <p className="text-slate-600 text-xs">Reports and insights</p>
            </div>
            <div className="border rounded-lg p-3">
              <h4 className="font-semibold text-slate-900 text-sm mb-1">📥 Import</h4>
              <p className="text-slate-600 text-xs">Bulk CSV import</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Dashboard Widgets</h3>
          <p className="text-slate-700 mb-3">
            Your dashboard displays key metrics at a glance:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Lead Statistics</strong>: Total leads, recent additions, conversion rates</li>
            <li><strong>Pipeline Overview</strong>: Leads by stage with visual indicators</li>
            <li><strong>Activity Stream</strong>: Recent activities across your team</li>
            <li><strong>Task Summary</strong>: Pending and overdue tasks</li>
            <li><strong>Quick Actions</strong>: Shortcuts to common operations</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Leads Table View</h3>
          <p className="text-slate-700 mb-3">
            The main leads table provides:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Search & Filter</strong>: Find leads by name, email, company, or stage</li>
            <li><strong>Sorting</strong>: Sort by any column (name, stage, score, date)</li>
            <li><strong>Status Badges</strong>: Visual indicators for enrichment status</li>
            <li><strong>Lead Scores</strong>: AI-calculated fit scores (0-100)</li>
            <li><strong>Owner Assignment</strong>: See who's responsible for each lead</li>
            <li><strong>Quick Actions</strong>: Enrich, view details, delete</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Top Bar Features</h3>
          <div className="space-y-3">
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Search Bar</h4>
              <p className="text-slate-700 text-sm">
                Global search to quickly find leads, companies, or contacts across your entire database.
              </p>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Notifications</h4>
              <p className="text-slate-700 text-sm">
                Bell icon shows system notifications, task reminders, and activity alerts.
              </p>
            </div>
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Profile Menu</h4>
              <p className="text-slate-700 text-sm">
                Access your profile settings, GDPR data rights, and logout option.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Analytics Page</h3>
          <p className="text-slate-700 mb-3">
            Navigate to Analytics to access detailed reports:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Overview</strong>: Summary statistics and pipeline distribution</li>
            <li><strong>Conversion Funnel</strong>: Stage-by-stage conversion analysis</li>
            <li><strong>Lead Sources</strong>: Performance by lead source</li>
            <li><strong>Performance Metrics</strong>: Win rates, response times, deal values</li>
            <li><strong>Team Stats</strong>: Individual team member performance</li>
          </ul>
        </section>

        <section>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 mb-2">💡 Dashboard Tips</h4>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Use filters to focus on specific lead segments</li>
              <li>• Check the dashboard daily for task reminders</li>
              <li>• Monitor your pipeline stages regularly</li>
              <li>• Export data when you need deeper analysis</li>
            </ul>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default Dashboard;